// Agent v2 `actions` (PHASE C1) — how one logged action reads in the admin.
// Pure; shared by the Approvals tab and the per-chat list so the two never
// disagree. Labels mirror worker/src/lib/agentActions.ts ACTION_LABEL.
import type { AgentActionItem } from '@/lib/api';

export const ACTION_LABEL: Record<string, string> = {
  set_title: 'Titre de couverture changé',
  no_title: 'Titre retiré de la couverture',
  cover_colour: 'Couleur de couverture changée',
  cover_swap: 'Couvertures remises sur les bons albums',
  cover_text_add: 'Texte ajouté sur la couverture',
  cover_text_remove: 'Texte retiré de la couverture',
  cover_font: 'Police du titre changée',
  photos_cut: 'Photos coupées recadrées',
  page_note: 'Modification de page envoyée aux notes',
  cover_photo: 'Photo du client mise en couverture',
  add_album: 'Un album de plus sur la commande',
  add_extra: 'Option ajoutée (Express / Relief 3D)',
  send_editor_link: 'Lien de l’éditeur envoyé',
  regenerate_preview: 'Aperçu 3D relancé',
  album_edit: 'Album modifié (photos / pages)',
  outro_toggle: 'Page de fin retirée / remise',
  price_override: 'Prix hors grille',
  discount: 'Remise',
  refund: 'Remboursement',
  free_reprint: 'Réimpression gratuite',
  cancel_paid_order: 'Annulation d’une commande payée',
};

export const STATUS_LABEL: Record<AgentActionItem['status'], string> = {
  done: 'Fait',
  undone: 'Annulé',
  failed: 'Échec',
  pending_approval: 'En attente de validation',
  approved: 'Validé',
  rejected: 'Refusé',
};

/** Tailwind classes for the status chip — the words always carry the meaning, never the colour alone. */
export const STATUS_TONE: Record<AgentActionItem['status'], string> = {
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  undone: 'bg-gray-50 text-gray-600 border-gray-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  pending_approval: 'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-gray-50 text-gray-600 border-gray-200',
};

export const actionTitle = (a: Pick<AgentActionItem, 'action'>): string => ACTION_LABEL[a.action] ?? a.action;

/** « +350 DH », « −79 DH », or null when it touches no price. */
export function impactLabel(dh: number | null | undefined): string | null {
  const n = Number(dh);
  if (dh == null || !Number.isFinite(n) || n === 0) return null;
  return `${n > 0 ? '+' : '−'}${Math.abs(Math.round(n))} DH`;
}

/** What it was about, in one short line: the title, the colour, the page, the links. */
export function argsLine(a: Pick<AgentActionItem, 'args'>): string | null {
  const g = a.args ?? {};
  const s = (k: string) => (typeof g[k] === 'string' && String(g[k]).trim() ? String(g[k]).trim() : null);
  if (s('title')) return `« ${s('title')} »`;
  if (s('colour')) return s('colour');
  if (typeof g.page === 'number') return `page ${g.page}${s('text') ? ` — « ${s('text')!.slice(0, 80)} »` : ''}`;
  if (s('text')) return `« ${s('text')!.slice(0, 80)} »`;
  if (typeof g.to === 'number') return `${g.to} albums`;
  if (Array.isArray(g.links) && g.links.length) return String(g.links[0]);
  if (s('font')) return s('font');
  // album_edit / outro_toggle (C2): « 3 modifications · 36 → 40 pages »
  if (s('summary')) return s('summary');
  return null;
}

/** The second line under the title: what changed, who decided, why it waits. */
export function detailLine(a: AgentActionItem): string {
  const parts: string[] = [];
  const what = argsLine(a);
  if (what) parts.push(what);
  if (a.changed?.length) parts.push(`changé : ${a.changed.join(', ')}`);
  const imp = impactLabel(a.impact_dh);
  if (imp) parts.push(imp);
  if (a.status === 'pending_approval' && a.approval_reason) parts.push(`à valider : ${a.approval_reason}`);
  if ((a.status === 'undone' || a.status === 'approved' || a.status === 'rejected') && a.decided_by) parts.push(`par ${a.decided_by}`);
  if (a.error) parts.push(a.error);
  return parts.join(' · ');
}

export function whenLabel(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
